/**
 * Engage SDK loader and helpers.
 *
 * This module is responsible for safely initializing and exposing the
 * Sitecore Engage SDK instance for browser use. It performs the following
 * responsibilities:
 * - Provides a placeholder cookie-consent check (`getCookieConsent`) which
 *   should be replaced with the application's real consent logic.
 * - Builds default event payloads via `eventDataBuilder`.
 * - Lazily initializes the Engage SDK with `loadEngage`, guarding against
 *   duplicate simultaneous initializations by storing a promise on
 *   `globalThis` using Symbols.
 * - Exposes synchronous and asynchronous accessors (`engage` and
 *   `onEngageLoaded`) and a `resetEngage` helper to clear global state
 *   (useful for tests).
 *
 * NOTE: The module intentionally performs `loadEngage()` on import because
 * Sitecore's SDK recommends initialization during application startup. The
 * loader is SSR-safe and will skip initialization when executed on the
 * server.
 */
import type { ISettingsParamsBrowser } from '@sitecore/engage/types/lib/settings/settings';
import { init as engageInit, ICustomEventInput, type Engage } from '@sitecore/engage';
import { env } from '../env';
import { fromGlobalThis, toGlobalThis } from '../typescript/_generics';
import { logError, logWarning, makeLoggableError } from '../util/errors';
import { baseEngageSettings } from './engage-config';
import '../typescript/withResolvers';

/** Symbol used to store the pending Engage initialization promise on globalThis. */
const ENGAGE_GLOBAL_SYMBOL = Symbol.for('@nmdp/sitecore.engage:promise');

/** Symbol used to store a resolved Engage instance on globalThis. */
const ENGAGE_RESOLVED_SYMBOL = Symbol.for('@nmdp/sitecore.engage:instance');

/**
 * Placeholder cookie consent check.
 *
 * Replace this with your application's actual cookie-consent implementation.
 * The loader will not initialize Engage unless this resolves `true`.
 *
 * @returns Promise that resolves to `true` when cookie consent has been
 * granted, or `false` otherwise.
 */
export const getCookieConsent = (): Promise<boolean> => Promise.resolve(true);

/**
 * Build a default event payload for Engage with sensible defaults.
 *
 * This helper merges `props` provided by the caller with a collection of
 * defaults derived from `baseEngageSettings` and the current browser
 * environment (page URL, referrer, language). The generic `TOutput` allows
 * callers to express a more specific output shape for TypeScript type
 * narrowing.
 *
 * Notes:
 * - `language` will only be returned when it matches one of the supported
 *   language codes for the application; otherwise it is omitted.
 * - This function accesses `window`/`document` so it must only be called in
 *   browser contexts.
 *
 * @template TOutput Type of the returned event payload.
 * @param props Optional custom properties that override defaults.
 * @returns Merged event payload for sending to Engage.
 */
export const eventDataBuilder = <TOutput = Partial<ICustomEventInput>>(
  props?: TOutput
): TOutput & ICustomEventInput => {
  let language: string | undefined = navigator?.language || navigator?.languages?.[0] || 'en';
  if (!['en', 'es-US'].includes(language)) {
    language = undefined;
  }
  return {
    pointOfSale: baseEngageSettings.pointOfSale,
    channel: baseEngageSettings.channel,
    currency: baseEngageSettings.currency,
    ...{
      page: window?.location?.href ? window.location.href : undefined,
      referrer: document?.referrer ? document.referrer : undefined,
      language,
    },
    ...(props ?? {}),
  } as unknown as TOutput & ICustomEventInput;
};

/** Minimal shape stored on `globalThis` while initialization is in-flight. */
type PartialEngageGlobal = {
  [ENGAGE_GLOBAL_SYMBOL]?: Promise<Engage | null>;
  [ENGAGE_RESOLVED_SYMBOL]?: Engage | null;
};

/** Finalized shape stored on `globalThis` after initialization is started. */
type EngageGlobal = PartialEngageGlobal & {
  [ENGAGE_GLOBAL_SYMBOL]: Promise<Engage | null>;
};

/**
 * Initialize the Sitecore Engage SDK in the browser.
 *
 * This function is idempotent and uses `globalThis` Symbols to ensure that
 * only a single initialization promise is ever in-flight. It will:
 *
 * - Skip initialization when the feature flag `FEATURE_FLAG_ENABLE_ENGAGE_SDK`
 *   is not enabled.
 * - Skip initialization when executed on the server.
 * - Check for cookie consent via `getCookieConsent()` before initializing.
 * - Call `engageInit()` with settings derived from `baseEngageSettings` and
 *   the `eventDataBuilder()` output.
 *
 * The function returns the resolved `Engage` instance, or `null` when the
 * SDK is not initialized (feature flag off, no consent, or error).
 *
 * Errors are logged via `logError` and the returned promise is rejected so
 * callers can observe failure.
 *
 * @returns Promise resolving to an `Engage` instance or `null`.
 */
const loadEngage = async (): Promise<Engage | null> => {
  // Feature flag check
  if (env.FEATURE_FLAG_ENABLE_ENGAGE_SDK !== true) {
    return null;
  }
  // Guard against multiple simultaneous calls
  const { [ENGAGE_GLOBAL_SYMBOL]: currentOperation } =
    fromGlobalThis<EngageGlobal>() ?? ({} as EngageGlobal);
  if (currentOperation) {
    return currentOperation;
  }
  // Create promise with external resolvers (polyfill for Promise.withResolvers)
  const thisOperation = Promise.withResolvers<Engage | null>();

  const resolvedPromise = thisOperation.promise.then((engageInstance) => {
    toGlobalThis<EngageGlobal>(
      {
        [ENGAGE_RESOLVED_SYMBOL]: engageInstance,
        [ENGAGE_GLOBAL_SYMBOL]: resolvedPromise,
      },
      true
    );
    return engageInstance;
  });
  toGlobalThis<EngageGlobal>({
    [ENGAGE_GLOBAL_SYMBOL]: resolvedPromise,
  });
  try {
    // HACK: We shouldn't be getting called on the server, but guard anyway.
    if (typeof window === 'undefined') {
      logWarning('Engage SDK: Load called on server - skipping initialization.');
      thisOperation.resolve(null);
      return null;
    }
    // Right now, this always returns true.  IMPORTANT: This is my friendly nag
    // reminder that at some point the little blurb in the privacy policy isn't
    // going to cut it, and we will need to have an actual cookie consent mechanism
    // in place before loading Engage or other trackers.  Plus, it's the do-no-evil
    // thing to do, so please don't forget to circle back to this when the time
    // comes.
    const consent = await getCookieConsent();
    if (!consent) {
      thisOperation.resolve(null);
      return null;
    }

    const engageInstance = await engageInit(
      eventDataBuilder<ISettingsParamsBrowser>({
        clientKey: baseEngageSettings.clientKey,
        cookieExpiryDays: baseEngageSettings.cookieExpiryDays,
        forceServerCookieMode: baseEngageSettings.forceServerCookieMode,
        includeUTMParameters: baseEngageSettings.includeUTMParameters ?? true,
        webPersonalization: baseEngageSettings.webPersonalization,
        targetURL: baseEngageSettings.targetURL,
        cookieDomain: baseEngageSettings.cookieDomain,
      })
    );
    thisOperation.resolve(engageInstance);
  } catch (error) {
    logError(
      'Engage SDK: Load',
      makeLoggableError({
        error,
      })
    );
    thisOperation.reject(error);
  }
  return resolvedPromise;
};

// Trigger initialization on module import — this mirrors Sitecore's
// recommended pattern. The loader itself is SSR-safe and will skip
// initialization when running on the server. However, during Jest tests
// we do NOT want to auto-initialize the browser SDK; tests should control
// initialization explicitly. See `resetEngage()` for test helpers.
// https://doc.sitecore.com/cdp/en/developers/api/initialization.html
const isJest =
  typeof process !== 'undefined' &&
  (process.env?.JEST_WORKER_ID != null || process.env?.NODE_ENV === 'test');

if (!isJest) {
  loadEngage();
} else {
  // Skip auto-initialization in Jest environments; tests can call
  // `loadEngage()` or `onEngageLoaded()` explicitly if needed.
}

/**
 * Overloads for `getEngage`:
 * - Call with no args to synchronously return the resolved `Engage` instance
 *   (or `undefined` if not yet initialized).
 * - Call with a callback to synchronously receive the instance (or
 *   `undefined`) as the argument.
 */
interface GetEngageOverloads {
  (): Engage | null | undefined;
  <TResult>(cb: (engageInstance: Engage | null | undefined) => TResult): TResult;
}

/**
 * Overloads for `onEngageLoaded`:
 * - Call with no args to receive a promise resolving to the `Engage` instance
 *   (or `null`).
 * - Call with a callback to receive the `Engage` instance when available.
 */
interface GetEngageAsyncOverloads {
  (): Promise<Engage | null>;
  <TResult>(cb: (engageInstance: Engage | null) => TResult): Promise<TResult>;
}

/**
 * Synchronous accessor for the Engage instance.
 *
 * This function reads the initialization state from `globalThis`. If the
 * SDK has not started initializing yet it will trigger `loadEngage()` and
 * return `undefined` (or call the callback with `undefined`). If a resolved
 * instance is available it is returned synchronously.
 *
 * Usage:
 * - `const instance = engage();`
 * - `engage(inst => { /* use inst *\\ });`
 *
 * @returns The `Engage` instance, `null`, or `undefined` depending on state.
 */
const getEngage: GetEngageOverloads = <T>(
  cb?: (engageInstance: Engage | null | undefined) => T
) => {
  const state = fromGlobalThis<EngageGlobal>();
  const engagePromise = state[ENGAGE_GLOBAL_SYMBOL];
  if (!engagePromise) {
    loadEngage();
    return cb ? cb(undefined) : undefined;
  }
  const engageInstance = state[ENGAGE_RESOLVED_SYMBOL];
  return cb ? cb(engageInstance) : engageInstance;
};

export { getEngage as engage };

/**
 * Asynchronous accessor that resolves when the Engage SDK is loaded.
 *
 * If initialization hasn't started it will kick off `loadEngage()` and
 * await the resulting promise. A callback may be supplied to synchronously
 * operate on the resolved instance once available.
 *
 * @returns Promise resolving to the `Engage` instance or `null`.
 */
export const onEngageLoaded: GetEngageAsyncOverloads = async <T>(
  cb?: (engageInstance: Engage | null) => T
) => {
  const state = fromGlobalThis<EngageGlobal>();
  let engagePromise = state[ENGAGE_GLOBAL_SYMBOL];
  if (!engagePromise) {
    engagePromise = loadEngage();
  }
  const engageInstance = await engagePromise;
  return cb ? cb(engageInstance) : engageInstance;
};

/**
 * Reset any Engage-related global state. This will remove the stored
 * symbols from `globalThis` so the SDK can be re-initialized. Useful for
 * tests that need to control initialization state.
 */
export const resetEngage = (): void => {
  toGlobalThis<EngageGlobal>({}, true);
};
